import type { ReactElement, ReactNode } from 'react';

/**
 * Standard section heading block for marketing page shells: an optional mono
 * eyebrow, the `<h2>`, and an optional sub-line. Every string is passed in
 * already-translated (callers resolve via `t()`); this component is
 * presentation only.
 *
 * The eyebrow is the redesign's letterspaced mono label — NOT the brand
 * gradient the previous design painted here. Reserving the gradient for the
 * things that ARE the brand (the logo, one emphasised phrase, the demo chrome)
 * is what keeps it from flattening into decoration; the same reasoning retired
 * the gradient CTA fill. `rtl:` resets come with it: Arabic and Hebrew have no
 * letter case, and wide tracking breaks Arabic's cursive joining, so the mono
 * treatment degrades to a plain small label there.
 */
export interface SectionHeaderProps {
  readonly eyebrow?: ReactNode;
  readonly sub?: ReactNode;
  /** id for the `<h2>`, so a section can wire `aria-labelledby`. */
  readonly id?: string;
  readonly align?: 'center' | 'start';
  /**
   * Paint for a dark band ground rather than the light page.
   *
   * Without it a `<SectionHeader>` inside a `tone="band"` Band draws
   * `--bd-ink` (near-black) on `--bd-band` (near-black) — a heading that is
   * invisible rather than merely low-contrast, and the one failure mode a
   * shared heading component must not have. The three tokens are the same ones
   * `typography.tsx` switches to, so the two cannot disagree about what a dark
   * band's ink is.
   */
  readonly onDark?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

export function SectionHeader({
  eyebrow,
  sub,
  id,
  align = 'center',
  onDark = false,
  className,
  children,
}: SectionHeaderProps): ReactElement {
  const alignment = align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl text-start';
  return (
    <div className={[alignment, className].filter(Boolean).join(' ')}>
      {eyebrow !== undefined && eyebrow !== null && eyebrow !== '' ? (
        <p
          className={`mb-4 font-mono rtl:font-sans text-[11px] uppercase tracking-[0.16em] rtl:normal-case rtl:tracking-normal ${
            onDark ? 'text-[color:var(--bd-band-eyebrow)]' : 'text-[color:var(--bd-subtle)]'
          }`}
        >
          {eyebrow}
        </p>
      ) : null}
      <h2
        id={id}
        className={`text-balance text-[clamp(1.85rem,3.2vw,2.5rem)] font-semibold leading-[1.08] tracking-[-0.042em] ${
          onDark ? 'text-[color:var(--bd-band-ink)]' : 'text-[color:var(--bd-ink)]'
        }`}
      >
        {children}
      </h2>
      {sub !== undefined && sub !== null && sub !== '' ? (
        <p
          className={`mt-4 text-pretty text-[1.05rem] leading-[1.62] ${
            onDark ? 'text-[color:var(--bd-band-body)]' : 'text-[color:var(--bd-body)]'
          }`}
        >
          {sub}
        </p>
      ) : null}
    </div>
  );
}
